import React, { useEffect, useRef, useCallback, useContext } from 'react'
import _ from 'lodash'
import { MaterialStatus, type FormAppRelayInterface, type FusionError, serializeFusionError, proposeMountRenderer, proposeUseOriginalRendererForDocgen, executePostRenderHacksForDocgen, selectDrugIcdCodesArgs, DrugIcd } from '@fusion-advantage/backcompat'
import type { FormRenderer } from '@fusion-advantage/formrender'
import { AppEnvironment, getDisplayIdentity, RootRouteContentType, RX_SCAN_MODES, sleep, WorkItemAction } from '@fusion-advantage/formlib'
import type { RendererModule } from './RendererModule'
import { useAppDispatch, useAppStore } from './redux/hooks'
import { subscribeAppFormRelay } from './lib/relay/subscribeAppFormRelay'
import { selectLegacyLocationQuery } from './redux/locationSlice'
import { insertEncounterFile } from './dal2/insertEncounterFile'
import { selectWritesDisabled } from './redux/selectWritesDisabled'
import { mustSelectEncounterFileDataUrl } from './dal2/selectEncounterFile'
import { apiAuditSecondaryCodesV3, apiAuditUnfilteredIcd } from './redux/apithunk/apiAudit'
import { updateRenderState } from './lib/relay/updateRenderState'
import { appFormLoadPhase1, appFormLoadPhase2 } from './lib/persistence/appFormLoad'
import { DocgenStatus, setAppContent, setDocgenStatus, setLdng, setRenderer } from './redux/appSlice'
import SwiftUIView from './plugins/swiftUIViewPlugin'
import { Capacitor } from '@capacitor/core'
import PluginContext from './plugins/PluginContext'
import { appFormSave } from './lib/persistence/appFormSave'
import { getJsBundle } from './lib/appInfo'
import { selectAllDrugIcd, selectDrugIcdByProductId } from './dal/ldbDrugIcd'
import { selectAllDrugProducts } from './dal/ldbDrugProduct'
import { selectMaterialStatus } from './dal/ldbMaterial'
import { appError, AppErrorArgs } from './redux/errorSlice'
import { DispatchableFormErrorType } from './redux/errorSlice'
import { CloudToDeviceBlobType, CloudToDeviceBlobUpnAll, selectCloudToDeviceBlob } from './dal/ldbCloudToDeviceBlob'
import { pushLogEntry } from './redux/logSlice'
import { gunzipSync } from 'fflate'
import { deleteRenderInstance, setRenderInstance } from './redux/thunk/renderInstanceStore'
import { resetAppFormState } from './redux/actions'
import { recordBackgroundLegacyDraftSaveRequest } from './redux/backgroundSlice'
import { compressPhoto } from './lib/compressPhoto'
import { Result } from 'neverthrow'
import { ldbUnfilteredIcdBulkInsert, selectHasUnfilteredIcds, selectUnfilteredIcd } from './dal/ldbUnfilteredIcd'

interface SetupRef {
	current: {
		detached: boolean
		cleanup: (() => void)|null
	}
}

const FormrenderMount = React.memo(() => {
	const appStore = useAppStore()
	const dispatch = useAppDispatch()
	const pluginCtx = useContext(PluginContext)
	const elRef = useRef<HTMLDivElement|null>(null)
	const rendererRef = useRef<FormRenderer|null>(null)
	const dbError = useCallback((err: FusionError, args?: Partial<AppErrorArgs>) => {
		dispatch(appError({
			err,
			type: DispatchableFormErrorType.DbError,
			alertMessage: 'Sorry, an unexpected local database error occurred; please contact support.',
			...(args ?? {}),
		}))
	}, [])
	const exitForm = useCallback(() => {
		const content = appStore.getState().app.content
		if(content.type === RootRouteContentType.Form) {
			if(content.action === WorkItemAction.ViewFormAsDocgen) {
				// the only time a form is exited in docgen
				// is if there was a load error
				dispatch(setDocgenStatus({status: DocgenStatus.Error }))
			}
		}
		dispatch(setLdng({form: false}))
		dispatch(setAppContent({type: RootRouteContentType.Dashboard}))
	}, [appStore, dispatch])
	const createFormAppRelay = useCallback((): FormAppRelayInterface => {
		const selectDrugIcdCodes: FormAppRelayInterface["selectDrugIcdCodes"] = async (args) => {
			let r: Result<DrugIcd[], FusionError>
			if(args.productId) {
				r = await selectDrugIcdByProductId(args.productId)	
			} else {
				r = await selectAllDrugIcd()
			}
			if(r.isErr()) {
				dbError(r.error)
				return { icdCodes: [] }
			}
			return { icdCodes: r.value }
		}
		return {
			updateRenderState: (renderState) => {
				dispatch(updateRenderState(renderState))
			},
			/**
			 * @deprecated: 20251120
			 * see comments in @fusion-advantage/backcompat Location.ts
			 */
			updateUserLocation: async () => {
				const locationQuery = selectLegacyLocationQuery(appStore.getState())
				return { locationQuery }
			},
			downloadFile: async (args) => {
				const encounterGuid = appStore.getState().render.form?.export?.Form.Encounter.Encounter_GUID
				if(!encounterGuid) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.downloadFile', 'expected Encounter_GUID but null')
					}))
					return null
				}
				// use render state's identity for the save,
				// because the dashboard session behind the form may have expired
				const user = getDisplayIdentity(appStore.getState().render)
				if(!user) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.downloadFile', 'expected user but null')
					}))
					return null
				}
				const r = await mustSelectEncounterFileDataUrl(encounterGuid, args.BlobName, user.upn)
				if(r.isErr()) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.downloadFile', r.error)
					}))
					return null
				}
				return { SasUrl: r.value }
			},
			uploadFile: async (args) => {
				// refuse rather than queue: a queued file would still be
				// uploaded by a later sync once writes are re-enabled
				if(selectWritesDisabled(appStore.getState())) {
					alert('Files cannot be uploaded in Training mode or branch preview.')
					return null
				}
				const encounterGuid = appStore.getState().render.form?.export?.Form.Encounter.Encounter_GUID
				if(!encounterGuid) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.uploadFile', 'expected Encounter_GUID but null')
					}))
					return null
				}
				// use render state's identity for the save,
				// because the dashboard session behind the form may have expired
				const user = getDisplayIdentity(appStore.getState().render)
				if(!user) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.uploadFile', 'expected user but null')
					}))
					return null
				}
				let file = args.File
				// don't bother with compressing unless the file is > 250kb;
				// we should make a more exhaustive check if the user is
				// ever uploading anything other than photos
				if(file.size > 250 * 1024) {
					const rCompress = await compressPhoto(file)
					if(rCompress.isOk()) {
						file = rCompress.value
					} else {
						// if it fails, be silent
						// and just proceed with the original blob
						dispatch(appError({
							err: rCompress.error,
							suppressAlert: true,
						}))
					}
				}
				const r = await insertEncounterFile(encounterGuid, file, user.upn)
				if(r.isErr()) {
					dispatch(appError({
						err: serializeFusionError('formAppRelay.uploadFile', r.error)
					}))
					return null
				}
				return { BlobName: r.value.id }
			},
			addNativePluginListener: (args) => {
				const remover = pluginCtx.addCtxListener(args.name, args.handler)
				return { remover }
			},
			showRxScanner: (args) => {
				if (Capacitor.isNativePlatform()) {
					SwiftUIView.present({
						viewName: 'cameraView',
						...args,
						scanMode: RX_SCAN_MODES[0].value,
						isDebugMode: appStore.getState().render.showDebugInfo,
					}).catch(console.error)
				}
			},
			showBarcodeScanner: (args) => {
				if (Capacitor.isNativePlatform()) {
					SwiftUIView.present({
						viewName: 'barcodeView',
						barcodeType: args.barcodeType,
					}).catch(console.error)
				}
			},
			queryDrugProducts: async () => {
				let r = await selectAllDrugProducts()
				if(r.isErr()) {
					dbError(r.error)
					return { products: [] }
				}
				return { products: r.value }
			},
			queryIcdCodes: async () => {
				return selectDrugIcdCodes({})
			},
			queryIcdCodesByProductId: async (productId: number) => {
				return selectDrugIcdCodes({productId})
			},
			selectDrugIcdCodes,
			queryMaterialStatus: async ({query}) => {
				const encounterGuid = appStore.getState().render.form?.export?.Form.Encounter.Encounter_GUID
				let r = await selectMaterialStatus(query.area, query.id, query.type, encounterGuid)
				if(r.isErr()) {
					dbError(r.error, { suppressAlert: true })
					return { status: MaterialStatus.Unknown }
				}
				return { status: r.value }
			},
			queryUnfilteredIcds: async ({icdCodeQuery}) => {
				const dateOfService = appStore.getState().render.form?.export?.Form.Encounter.DateOfService
				if(!dateOfService) {
					alert('[queryUnfilteredIcds] cannot query ICDs: no date of service available')
					return { icdCodes: [] }
				}
				const rHas = await selectHasUnfilteredIcds(dateOfService)
				if(rHas.isErr()) {
					dbError(rHas.error, { suppressAlert: false })
					return { icdCodes: [] }
				}
				if(rHas.value !== true) {
					const rGet = await dispatch(apiAuditUnfilteredIcd(dateOfService))
					if(rGet.isErr()) {
						// error message already displayed,
						// nothing further to do
						return { icdCodes: [] }
					}
					const rInsert = await ldbUnfilteredIcdBulkInsert(dateOfService, rGet.value)
					if(rInsert.isErr()) {
						dbError(rInsert.error, { suppressAlert: false })
						return { icdCodes: [] }
					}
				}
				const rSelect = await selectUnfilteredIcd(dateOfService, icdCodeQuery)
				if(rSelect.isErr()) {
					dbError(rSelect.error, { suppressAlert: false })
					return { icdCodes: [] }
				}
				return { icdCodes: rSelect.value }
			},
			querySecondaryCodes: async ({ models }) => {
				const dateOfService = appStore.getState().render.form?.export?.Form.Encounter.DateOfService
				const r = await dispatch(apiAuditSecondaryCodesV3(models, dateOfService))
				if(r.isErr()) {
					// error message already displayed,
					// nothing further to do
					return null
				}
				return { secondaryCodes: r.value }
			},
			exitForm,
			saveFormDraft: async ({ closeOnSuccess }) => {
				if(!rendererRef.current?.appFormRelay) {
					alert('[saveFormDraft] Error: appFormRelay not available')
					return
				}
				// this is now only used by legacy renderers;
				// updated approach is backgroundDraftSaveMw queries appFormRelay.getLastChosenAt()
				if(!closeOnSuccess) {
					dispatch(recordBackgroundLegacyDraftSaveRequest())
					return
				}
				// save and return to dashboard
				await dispatch(appFormSave({
					appFormRelay: rendererRef.current.appFormRelay,
					closeOnSuccess: true,
				}))
			},
			submitForm: async (args) => {
				if(!rendererRef.current?.appFormRelay) {
					alert('[submitForm] Error: appFormRelay not available')
					return
				}
				await dispatch(appFormSave({
					appFormRelay: rendererRef.current.appFormRelay,
					submitToCode: args.codeNext,
					validityByCid: args.validityByCid,
					closeOnSuccess: true,
				}))
			},
		}
	}, [appStore, dispatch, dbError])
	useEffect(() => {
		dispatch(setLdng({form: true}))
		dispatch(resetAppFormState())
		const contentSpec = appStore.getState().app.content
		if(contentSpec.type !== RootRouteContentType.Form) {
			dispatch(appError({
				err: serializeFusionError('FormrenderMount', 'app content expected to be type form'),
			}))
			return
		}
		const { renderInstanceId } = contentSpec
		// IF the ref to the DOM element hasn't been populated yet (first render)
		// OR, the formRenderer has already been created
		// set up nothing further
		if(
			!elRef.current ||
			rendererRef.current
		) {
			return
		}
		const setupRef: SetupRef = {
			current: {
				detached: false,
				cleanup: null,
			}
		}
		// https://stackoverflow.com/questions/73459382/react-18-async-way-to-unmount-root
		// introducing a slight delay with setTimeout will prevent a sudden
		// mount and unmount caused by React.StrictMode to fire the setup
		// stuff twice
		window.setTimeout(async () => {
			if(setupRef.current!.detached) {
				return
			}
			dispatch(pushLogEntry(`[FormrenderMount] call appFormLoadPhase1 render instance ${renderInstanceId}`))
			const rPhase1 = await dispatch(appFormLoadPhase1(contentSpec))
			if(rPhase1.isErr()) {
				if(rPhase1.error.message !== '') {
					dispatch(appError({
						err: rPhase1.error,
					}))
				}
				exitForm()
				return
			}
			const { exp, draftExportSupplement } = rPhase1.value
			const forceDevRenderer = appStore.getState().app.forceDevRenderer
			let rendererNumber = exp.Form.Encounter.FormRenderNbr
			let renderModule: RendererModule
			dispatch(pushLogEntry(`[FormrenderMount] begin renderer load`))
			// using a published renderer
			if(_.isNumber(rendererNumber) && !forceDevRenderer) {
				if(contentSpec.action === WorkItemAction.ViewFormAsDocgen) {
					const rUseOriginal = proposeUseOriginalRendererForDocgen(rendererNumber)
					if(!rUseOriginal.isOk()) {
						rendererNumber = rUseOriginal.error
					}
				}
				// branch is published, has renderer
				dispatch(pushLogEntry(`[FormrenderMount] using renderer ${rendererNumber}`))
				const rProposeMount = proposeMountRenderer(rendererNumber)
				if(!rProposeMount.isOk()) {
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', rProposeMount.error)
					}))
					exitForm()
					return
				}
				dispatch(setRenderer(rendererNumber))
				const currentBundle = getJsBundle()
				const minBundle = exp.Form.Encounter.MinimumBundle
				const minBundleMet = (_.isNumber(currentBundle) && _.isNumber(minBundle)) ? (currentBundle >= minBundle) : false
				dispatch(pushLogEntry(`[FormrenderMount] minBundle ${minBundleMet}`))
				dispatch(pushLogEntry(`[FormrenderMount] currentBundle ${currentBundle} ${minBundleMet ? '🏆' : '💩'}`))
				if(!minBundleMet) {
					// expected, actionable condition (app out of date for this
					// encounter's renderer) — not a crash. Surface a friendly,
					// specific alert to the user and keep the bundle numbers in
					// the logged error for diagnostics. Suppress the debugger
					// break since this is handled, not unexpected.
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', `minimum bundle not met (${currentBundle} < ${minBundle})`),
						alertMessage: 'This encounter requires a newer version of the app.  Please update to the latest version to open it.  If this message persists, please contact support.',
						suppressDebugger: true,
					}))
					exitForm()
					return
				}
				// const moduleUrl = `https://static.fusion-advantage.com/app-renderer/${rendererNumber}/formrender/formrender.js`
				const moduleBlobId = String(rendererNumber)
				const rModule = await selectCloudToDeviceBlob({
					type: CloudToDeviceBlobType.Renderer,
					id: moduleBlobId,
					upn: CloudToDeviceBlobUpnAll,
				})
				if(rModule.isErr()) {
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', rModule.error),
					}))
					exitForm()
					return
				}
				if(!rModule.value) {
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', `expected renderer module '${moduleBlobId}' but null`),
					}))
					exitForm()
					return
				}
				const moduleGzBytes = rModule.value.data
				dispatch(pushLogEntry(`[FormrenderMount] renderer length ${moduleGzBytes.byteLength} bytes`))
				try {
					const decompressed = gunzipSync(moduleGzBytes)
					dispatch(pushLogEntry(`[FormrenderMount] gunzip complete, length ${decompressed.byteLength}`))
					dispatch(pushLogEntry(`[FormrenderMount] creating object URL...`))
					// const moduleUrl = 'data:text/javascript;base64,' + moduleB64
					const blob = new Blob([decompressed as BlobPart], { type: 'application/javascript' })
					const url = URL.createObjectURL(blob)
					dispatch(pushLogEntry(`[FormrenderMount] importing...`))
					// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/import
					renderModule = await import(url)
					dispatch(pushLogEntry('[FormrenderMount] renderer import complete; revoking object URL to free memory'))
					URL.revokeObjectURL(url)
				} catch(ex) {
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', ex),
					}))
					exitForm()
					return
				}
			} else {
				if(forceDevRenderer || appStore.getState().app.environment === AppEnvironment.Development) {
					dispatch(pushLogEntry(`[FormrenderMount] using local built-in dev renderer`))
					dispatch(setRenderer(null))
					// in theory this should allow code-splitting to reduce the
					// primary bundle size, but because we are using components
					// from the library in other places, this doesn't make any
					// difference over a normal import right now
					// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/import
					// https://vite.dev/guide/features#dynamic-import
					renderModule = await import('@fusion-advantage/formrender')
				} else {
					dispatch(appError({
						err: serializeFusionError('FormrenderMount', 'renderer number is not available; please contact support.'),
					}))
					exitForm()
					return
				}
			}
			dispatch(pushLogEntry(`[FormrenderMount] renderer load complete`))
			rendererRef.current = renderModule.createFormRenderer({
				el: elRef.current!,
				formAppRelay: createFormAppRelay(),
			})
			dispatch(setRenderInstance(renderInstanceId, rendererRef.current))
			const { root, appFormRelay } = rendererRef.current
			const unsubscribe = subscribeAppFormRelay(appStore, appFormRelay)
			setupRef.current!.cleanup = () => {
				dispatch(pushLogEntry(`[FormrenderMount] cleanup render instance ${renderInstanceId}`))
				unsubscribe()
				dispatch(deleteRenderInstance(renderInstanceId))
				appFormRelay.cleanup()
				// https://stackoverflow.com/questions/73459382/react-18-async-way-to-unmount-root
				setTimeout(() => {
					root.unmount()
				}, 50)
			}
			// push initial renderState to form
			dispatch(updateRenderState({form: null}))
			const loadForm = async () => {
				dispatch(pushLogEntry(`[FormrenderMount] loadForm ${renderInstanceId}`))
				const rPhase2 = await dispatch(appFormLoadPhase2({
					appFormRelay,
					action: contentSpec.action,
					exp,
					draftExportSupplement,
					onlyPageIdx: contentSpec.onlyPageIdx,
					openedFromLink: contentSpec.openedFromLink,
				}))
				if(setupRef.current.detached || rPhase2.isErr()) {
					if(rPhase2.isErr() && rPhase2.error.message !== '') {
						dispatch(appError({
							err: rPhase2.error,
						}))
					}
					exitForm()
					return
				}
			}
			await loadForm()
			dispatch(setLdng({form: false}))
			dispatch(pushLogEntry(`[FormrenderMount] content load complete`))
			setTimeout(async () => {
				if(contentSpec.action === WorkItemAction.ViewFormAsDocgen && rendererNumber) {
					const rHacks = executePostRenderHacksForDocgen(rendererNumber)
					if(rHacks.isErr()) {
						dispatch(appError({
							err: rHacks.error,
						}))
						dispatch(setDocgenStatus({status: DocgenStatus.Error }))
					} else {
						// dan's request to wait 2.5s
						// before waving the success flag
						await sleep(2500)
						dispatch(setDocgenStatus({
							status: DocgenStatus.Success,
							height: document.documentElement.scrollHeight,
						}))
					}
				}
			}, 1000)
		}, 50)
		return () => {
			setupRef.current!.detached = true
			if(setupRef.current?.cleanup) {
				setupRef.current.cleanup()
			}
			dispatch(resetAppFormState())
		}
	})
	return (
		<div ref={elRef} />
	)
})

export default FormrenderMount
